import { Eye, FilePen, RefreshCw, RotateCw, Send, Upload } from "lucide-react";
import PropTypes from "prop-types";
import Datatable from "@components/datatable";
import StatusChip from "@components/ui-components/status-chip";
import LoadingButton from "@components/ui-components/loading-button";
import { ComponentSpinner } from "@components/spinner";
import { formatDateTime } from "@helpers/date";
import ContractConfirmModal from "@panel/shared/contract/components/contract-confirm-modal";
import ContractPreviewModal from "@panel/shared/contract/components/contract-preview-modal";
import SignaturesCell from "@panel/shared/contract/components/signatures-cell";
import {
    CONTRACT_ACCEPT_ATTR,
    PUBLISHER_CONTRACT_STATUS,
    PUBLISHER_CONTRACT_STATUS_LABEL,
    PUBLISHER_CONTRACT_STATUS_TONE
} from "@panel/shared/contract/misc/constants";
import { formatFileSize, publisherViewLabelOf } from "@panel/shared/contract/misc/helpers";
import useTripartiteAgreement from "./hooks/use-tripartite-agreement";
import {
    PUBLISHER_TIME_COPY,
    RETRY_COPY,
    SEND_COPY,
    signedSummary,
    TRIPARTITE_COPY
} from "./misc/constants";

// When the row last moved: signed, sent, or not sent yet.
const timeOf = (row) => {
    if (row.status === PUBLISHER_CONTRACT_STATUS.SIGNED) return `${PUBLISHER_TIME_COPY.signed} ${row.completedAt ? formatDateTime(row.completedAt) : ""}`;
    if (row.sentAt) return `${PUBLISHER_TIME_COPY.sent} ${formatDateTime(row.sentAt)}`;
    return PUBLISHER_TIME_COPY.notSent;
};

// Ops' contract view for one brand: a single tripartite agreement (Trackier,
// brand, publisher) uploaded once, then sent to the brand's publishers — all
// at once or one by one. Each publisher row is that publisher's own contract.
const BrandContract = ({ brandId, brandName }) => {
    const {
        template, hasTemplate, templateLoading,
        uploading, openPicker, inputProps, confirmModal, onViewTemplate,
        publishers, counts, pagination, publishersLoading,
        handlePageChange, handlePerRowsChange, onRefresh,
        canSendAll, sendingAll, onSendAll, isSending, onSendOne,
        canRetry, retrying, onRetry,
        onViewPublisher, previewModal
    } = useTripartiteAgreement(brandId, brandName);

    const columns = [
        {
            Header  : "Publisher",
            accessor: "name",
            Cell    : ({ value, row }) => (
                <div className="min-w-0">
                    <p className="truncate font-medium text-gray-900">{value}</p>
                    {row.email && <p className="mt-0.5 truncate text-xs text-muted">{row.email}</p>}
                </div>
            )
        },
        {
            Header  : "Status",
            accessor: "status",
            Cell    : ({ value }) => (
                <StatusChip tone={PUBLISHER_CONTRACT_STATUS_TONE[value]} label={PUBLISHER_CONTRACT_STATUS_LABEL[value]} />
            )
        },
        {
            Header  : "Signatures",
            accessor: "signatures",
            Cell    : ({ value }) => <SignaturesCell signatures={value} />
        },
        {
            Header  : "Time",
            accessor: "sentAt",
            Cell    : ({ row }) => <span className="whitespace-nowrap text-sm text-muted">{timeOf(row)}</span>
        },
        {
            Header  : "Contract",
            accessor: "contractId",
            Cell    : ({ row }) => (
                <div className="flex items-center justify-end gap-2">
                    {/* Not sent, or sending — the send stays disabled while queued. */}
                    {[PUBLISHER_CONTRACT_STATUS.NOT_SENT, PUBLISHER_CONTRACT_STATUS.QUEUED].includes(row.status) ? (
                        <LoadingButton
                            color="dark"
                            size="xs"
                            icon={Send}
                            text={SEND_COPY.one}
                            loadingText={SEND_COPY.sending}
                            loading={isSending(row) || row.status === PUBLISHER_CONTRACT_STATUS.QUEUED}
                            disabled={!hasTemplate}
                            tooltip={hasTemplate ? "" : TRIPARTITE_COPY.awaitUpload}
                            onClick={() => onSendOne(row)}
                            testId="send-agreement-publisher" />
                    ) : (
                        <LoadingButton
                            color="neutral"
                            size="xs"
                            icon={Eye}
                            text={publisherViewLabelOf(row)}
                            onClick={() => onViewPublisher(row)}
                            testId="view-publisher-contract" />
                    )}
                </div>
            )
        }
    ];

    return (
        <div className="space-y-4">
            <section className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
                <div className="flex flex-wrap items-center gap-3">
                    <FilePen className="h-5 w-5 text-gray-700" />
                    <h2 className="text-base font-semibold text-gray-900">{TRIPARTITE_COPY.title}</h2>
                </div>
                <p className="mt-2 text-sm text-muted">{TRIPARTITE_COPY.description(brandName)}</p>
                <p className="mt-1 text-xs text-muted">{TRIPARTITE_COPY.placeholders}</p>

                {templateLoading ? (
                    <div className="flex justify-center py-6">
                        <ComponentSpinner />
                    </div>
                ) : hasTemplate ? (
                    <div className="mt-4 flex flex-wrap items-center justify-between gap-4 rounded-lg border border-gray-200 bg-light/40 p-3">
                        <div className="min-w-0">
                            <div className="flex flex-wrap items-center gap-2">
                                <p className="truncate text-sm font-medium text-gray-900">{template.name}</p>
                                {template.version !== null && <span className="text-xs text-muted">v{template.version}</span>}
                            </div>
                            <p className="mt-0.5 text-xs text-muted">
                                {formatFileSize(template.size)}
                                {template.createdAt && ` · Uploaded ${formatDateTime(template.createdAt)}`}
                                {template.createdBy && ` by ${template.createdBy}`}
                            </p>
                        </div>
                        <div className="flex shrink-0 items-center gap-2">
                            <LoadingButton
                                color="neutral"
                                size="sm"
                                icon={Eye}
                                text="View"
                                onClick={onViewTemplate}
                                testId="view-agreement" />
                            <LoadingButton
                                color="neutral"
                                size="sm"
                                icon={RefreshCw}
                                text="Replace"
                                loadingText={TRIPARTITE_COPY.uploading}
                                loading={uploading}
                                onClick={openPicker}
                                testId="replace-agreement" />
                        </div>
                    </div>
                ) : (
                    <>
                        <p className="mt-3 text-sm text-gray-700">{TRIPARTITE_COPY.empty}</p>
                        <LoadingButton
                            className="mt-4"
                            color="dark"
                            icon={Upload}
                            text={TRIPARTITE_COPY.upload}
                            loadingText={TRIPARTITE_COPY.uploading}
                            loading={uploading}
                            onClick={openPicker}
                            testId="upload-agreement" />
                    </>
                )}
                {/* One picker serves both upload and replace. */}
                <input {...inputProps} accept={CONTRACT_ACCEPT_ATTR} className="hidden" />
            </section>

            <section className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
                <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-200 px-5 py-4">
                    <div>
                        <h3 className="text-base font-semibold text-gray-900">Publishers</h3>
                        <p className="mt-0.5 text-xs text-muted">{signedSummary(counts)}</p>
                    </div>
                    <div className="flex items-center gap-2">
                        <LoadingButton
                            color="neutral"
                            size="sm"
                            icon={RotateCw}
                            text="Refresh"
                            loading={publishersLoading}
                            onClick={onRefresh}
                            testId="refresh-agreement-publishers" />
                        {canRetry && (
                            <LoadingButton
                                color="neutral"
                                size="sm"
                                text={RETRY_COPY.button}
                                loading={retrying}
                                tooltip={RETRY_COPY.tooltip}
                                onClick={onRetry}
                                testId="retry-agreement-publishers" />
                        )}
                        <LoadingButton
                            color="dark"
                            size="sm"
                            icon={Send}
                            text={SEND_COPY.all}
                            loadingText={SEND_COPY.sending}
                            loading={sendingAll}
                            disabled={!canSendAll}
                            tooltip={!hasTemplate ? TRIPARTITE_COPY.awaitUpload : canSendAll ? "" : SEND_COPY.allSent}
                            onClick={onSendAll}
                            testId="send-agreement-publishers" />
                    </div>
                </div>
                <Datatable
                    columns={columns}
                    data={publishers}
                    loading={publishersLoading}
                    pagination={{ count: pagination.total, page: pagination.page, limit: pagination.limit }}
                    handlePageChange={handlePageChange}
                    handlePerRowsChange={handlePerRowsChange}
                    customNoData={<span className="text-sm text-muted">{TRIPARTITE_COPY.noPublishers}</span>} />
            </section>

            <ContractConfirmModal {...confirmModal} />

            <ContractPreviewModal {...previewModal} />
        </div>
    );
};

BrandContract.propTypes = {
    brandId  : PropTypes.string.isRequired,
    brandName: PropTypes.string
};

export default BrandContract;
